import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useLocation, useParams, Navigate } from "react-router-dom";
import "./App.css";
import { useAnalyses, useAnalysisBody, useMediaGuide, useComments, useVote, useCommunityData, timeAgo } from "./useData";
import { supabase } from "./supabaseClient";
import AdminPanel from "./AdminPanel";
import { getPushStatus, enablePush, lastPushError } from "./pushNotifications";
import { Capacitor } from "@capacitor/core";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfUse from "./TermsOfUse";
import CookieConsent, { reopenCookieChoice, cookieSettingsAvailable } from "./CookieConsent";
import { checkText } from "./moderation";
import TariffCalculator from "./pages/TariffCalculator";
import ComplaintLine from "./pages/ComplaintLine";
import { hasAcceptedPostingTerms, acceptPostingTerms } from "./postingTerms";
import { getWatched, toggleWatched, WATCHLIST_EVENT } from "./follows";
import ReadingSize, { initReadingSize } from "./ReadingSize";
import seoMeta from "./data/seo-meta.json";


// Update this once the custom domain is live (currently on Netlify's free subdomain)
export const SITE_URL = "https://betweenthenews.com";

// ===== SEO METADATA (src/data/seo-meta.json) =====
// Per-piece search metadata and the noindex list live in a bundled JSON file until
// the analyses table gets matching columns. Keyed by DB id (the id in the URL).
const NOINDEX_IDS = new Set((seoMeta.noindex || []).map(Number));
const SEO_PIECES = seoMeta.pieces || {};
export function seoFor(post) {
  return (post && SEO_PIECES[String(post.id)]) || {};
}
export function isNoindex(post) {
  return !!post && NOINDEX_IDS.has(Number(post.id));
}

// prerender.cjs sets window.__BTN_PRERENDER before the app boots. In that mode the
// static snapshot we hand to crawlers carries the article and its links, not the
// comment box, newsletter form and cookie banner (which are 50%+ of the words on a
// page otherwise). Real visitors never see this flag, so they get the full UI.
const IS_PRERENDER = typeof window !== "undefined" && window.__BTN_PRERENDER === true;

// A <title> that fits a search result (~60 chars): seo_title if we set one,
// otherwise the headline's first sentence, otherwise a cut at a word boundary.
export function searchTitle(post) {
  const seo = seoFor(post);
  if (seo.seo_title) return seo.seo_title;
  const headline = String(post.headline || "").trim();
  if (headline.length <= 60) return headline;
  const first = (splitSentences(headline)[0] || headline).trim();
  if (first.length <= 70) return first;
  const cut = headline.slice(0, 60);
  return `${cut.slice(0, cut.lastIndexOf(" ") > 30 ? cut.lastIndexOf(" ") : 60).trim()}…`;
}

// Sentence splitter without regex lookbehind (older iOS Safari throws on it at parse time).
function splitSentences(text) {
  return String(text || "").match(/[^.!?]+(?:[.!?]+["”’)]?|$)/g) || [String(text || "")];
}

// Meta description at a sentence boundary instead of a mid-word "..." cut.
export function firstSentences(text, max = 155) {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const sentences = splitSentences(clean).map((s) => s.trim()).filter(Boolean);
  let out = "";
  for (const s of sentences) {
    if ((out + " " + s).trim().length > max) break;
    out = `${out} ${s}`.trim();
  }
  if (out.length >= 60) return out;
  const cut = clean.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" ")).trim()}…`;
}

// Bodies are stored as paragraphs separated by a lone "*" line.
function bodyParagraphs(text) {
  return String(text || "")
    .split(/\n\s*\*\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

// Paragraph/TL;DR/FAQ text may contain **bold** spans and, in trackers,
// [label](/internal/path/) cross-links; render both without a full markdown
// parser since that's the only syntax the authoring pipeline emits.
function renderRichText(text, keyPrefix) {
  const str = String(text || "");
  const re = /\*\*([^*]+)\*\*|\[([^\]\n]+)\]\((\/[^\s)]+)\)/g;
  const parts = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(str))) {
    if (m.index > last) parts.push(str.slice(last, m.index));
    if (m[1] !== undefined) {
      parts.push(<strong key={`${keyPrefix}-${i}`}>{m[1]}</strong>);
    } else {
      parts.push(
        <Link key={`${keyPrefix}-${i}`} to={m[3]}>
          {m[2]}
        </Link>
      );
    }
    i += 1;
    last = m.index + m[0].length;
  }
  if (parts.length === 0) return str;
  if (last < str.length) parts.push(str.slice(last));
  return parts;
}

export function slugify(text) {
  return (text || "")
    .toLowerCase()
    .replace(/['"“”‘’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function analysisPath(post) {
  // Trailing slash matches the prerendered build/analysis/:id/:slug/index.html
  // directory Netlify actually serves — avoids an extra 301 redirect hop for
  // freshly-shared links and crawlers.
  return `/analysis/${post.id}/${slugify(post.headline)}/`;
}

export function outletPath(outlet) {
  return `/guide/${slugify(outlet.name)}/`;
}

// The media_guide table spells some outlets differently than the analyses
// table ("Washington Post" vs "The Washington Post"). Canonicalize both sides
// so outlet pages and counts actually find their analyses.
const OUTLET_ALIASES = {
  "washington post": "the washington post",
  "new york times": "the new york times",
  "rt (russia today)": "russia today (rt)",
  "the associated press": "ap",
  "associated press": "ap",
};
function canonOutletName(name) {
  const n = (name || "").toLowerCase().trim();
  return OUTLET_ALIASES[n] || n;
}
export function sourceMatchesOutlet(source, outletName) {
  return canonOutletName(source) === canonOutletName(outletName);
}

// "Is X biased? / Who owns X?" deep-dive Guide articles, keyed by canonical
// outlet name → analyses.number. Extend as each outlet gets its page.
const OUTLET_DEEP_DIVES = {
  "npr": "247",
  "fox news": "248",
  "al jazeera": "249",
  "bbc news": "250",
  "reuters": "251",
  "euronews": "252",
  "cnn": "266",
  "the new york times": "267",
  "the guardian": "268",
  "russia today (rt)": "269",
  "the washington post": "279",
  "the sun": "280",
  "ap": "351",
  "bloomberg": "352",
  "the wall street journal": "363",
  "daily mail": "364",
  "nbc news": "377",
  "france 24": "378",
  "the mirror": "380",
  "cgtn": "381",
};
function deepDiveFor(analyses, outletName) {
  const num = OUTLET_DEEP_DIVES[canonOutletName(outletName)];
  return num ? (analyses || []).find((a) => a.number === num) : null;
}

// ===== TRACKERS (living explainer pages, category "Tracker") =====
// Hidden from browsing feeds like Guide rows, but each has its own indexable URL.
// Tracker bodies live in `decoded` with marker sections:
// ###TLDR### bullet lines, ###BODY### main text (* separators), ###FAQ### Q:/A: pairs.
const FEED_HIDDEN_CATEGORIES = ["Guide", "Tracker"];
function inFeed(a) {
  return !FEED_HIDDEN_CATEGORIES.includes(a.category);
}

function parseTrackerSections(decoded) {
  const text = decoded || "";
  if (!text.includes("###BODY###")) return { tldr: [], body: text, faq: [] };
  const tldrPart = text.includes("###TLDR###")
    ? text.split("###TLDR###")[1].split("###BODY###")[0]
    : "";
  const rest = text.split("###BODY###")[1] || "";
  const [bodyPart, faqPart = ""] = rest.split("###FAQ###");
  const tldr = tldrPart
    .split("\n")
    .map((l) => l.replace(/^[-•]\s*/, "").trim())
    .filter(Boolean);
  const faq = [];
  let current = null;
  for (const line of faqPart.split("\n")) {
    const t = line.trim();
    if (t.startsWith("Q:")) {
      if (current && current.a) faq.push(current);
      current = { q: t.slice(2).trim(), a: "" };
    } else if (t.startsWith("A:") && current) {
      current.a = t.slice(2).trim();
    } else if (current && current.a && t) {
      current.a += ` ${t}`;
    }
  }
  if (current && current.a) faq.push(current);
  return { tldr, body: bodyPart.trim(), faq };
}

function formatDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// Complaint Line cases share the comments table and store the reader's
// objection after a " ||| " marker (see pages/ComplaintLine.js). Community
// shows the same rows, so strip the marker rather than leaking it on screen.
const COMPLAINT_SEP = " ||| ";
function displayCommentText(text) {
  const raw = String(text || "");
  const i = raw.indexOf(COMPLAINT_SEP);
  if (i === -1) return raw;
  return `${raw.slice(0, i)} — "${raw.slice(i + COMPLAINT_SEP.length)}"`;
}

// Re-render whichever screen is mounted when the watchlist changes, wherever
// the change came from (a card, the manage sheet, another tab).
function useWatchlist() {
  const [watched, setWatched] = useState(getWatched);
  useEffect(() => {
    const sync = () => setWatched(getWatched());
    window.addEventListener(WATCHLIST_EVENT, sync);
    return () => window.removeEventListener(WATCHLIST_EVENT, sync);
  }, []);
  return watched;
}

// ===== NEWSLETTER =====
function NewsletterBox() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) || !supabase || status === "sending") return;
    setStatus("sending");
    const { error } = await supabase.from("newsletter_subscribers").insert({ email: trimmed });
    // 23505 = already subscribed with this email; treat as success
    if (!error || error.code === "23505") setStatus("done");
    else setStatus("error");
  };

  if (status === "done") {
    return (
      <div className="newsletter-box">
        <div className="newsletter-done">{"✉️"} You're in. Sharpest takes, straight to your inbox.</div>
      </div>
    );
  }

  return (
    <div className="newsletter-box">
      <div className="newsletter-title">The week's sharpest media takes, in your inbox</div>
      <form className="newsletter-form" onSubmit={submit}>
        <input
          type="email"
          className="newsletter-input"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Email address"
          required
        />
        <button type="submit" className="newsletter-btn" disabled={status === "sending"}>
          {status === "sending" ? "..." : "Subscribe"}
        </button>
      </form>
      {status === "error" && <div className="newsletter-error">Something went wrong — try again.</div>}
    </div>
  );
}

// ===== BOTTOM NAVIGATION =====
function Footer({ onPrivacyClick }) {
  return (
    <footer className="site-footer">
      {!IS_PRERENDER && <NewsletterBox />}
      <div className="footer-links">
        <Link className="footer-link" to="/guide/">Media Guide</Link>
        <Link className="footer-link" to="/trackers/">Trackers &amp; tools</Link>
        <Link className="footer-link" to="/explore/">Explore</Link>
        <Link className="footer-link" to="/about/">About &amp; how we work</Link>
        <button className="footer-link" onClick={onPrivacyClick}>Privacy Policy</button>
        <Link className="footer-link" to="/terms/">Terms of Use</Link>
        {cookieSettingsAvailable() && (
          <button className="footer-link" onClick={reopenCookieChoice}>Cookie settings</button>
        )}
        <a className="footer-link" href="mailto:privacy@betweenthenews.com">privacy@betweenthenews.com</a>
        <a className="footer-link" href="/feed.xml">RSS</a>
      </div>
      <div className="footer-copy">&copy; 2026 Between the News</div>
    </footer>
  );
}

function BottomNav({ active, onNavigate }) {
  const items = [
    { id: "home", icon: "\u{1F3E0}", label: "Home" },
    { id: "explore", icon: "\u{1F50D}", label: "Explore" },
    { id: "complaint-line", icon: "\u{1F4E3}", label: "Complaints" },
    { id: "community", icon: "\u{1F4AC}", label: "Community" },
    { id: "guide", icon: "\u{1F4DA}", label: "Media Guide" },
  ];
  return (
    <nav className="bottom-nav">
      {items.map((item) => (
        <Link
          key={item.id}
          to={item.id === "home" ? "/" : `/${item.id}/`}
          className={`nav-item ${active === item.id ? "active" : ""}`}
          style={{ textDecoration: "none" }}
          onClick={(e) => {
            e.preventDefault();
            onNavigate(item.id);
          }}
        >
          <span className="nav-icon">{item.icon}</span>
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

// ===== HEADER =====
function Header() {
  // 'unsupported' (web) | 'prompt' | 'granted' | 'denied'
  const [pushStatus, setPushStatus] = useState("unsupported");
  const [pushNote, setPushNote] = useState("");

  useEffect(() => {
    getPushStatus().then(setPushStatus);
  }, []);

  const handleBell = async () => {
    if (!Capacitor.isNativePlatform()) {
      setPushNote("Notifications work in the mobile app.");
    } else if (pushStatus === "granted") {
      setPushNote("Notifications are on — new analyses will find you.");
    } else {
      // Retry on every tap, even after an earlier failure.
      const result = await enablePush();
      setPushStatus(result);
      setPushNote(
        result === "granted"
          ? "Notifications are on — new analyses will find you."
          : result === "denied"
          ? "Notifications are off. Enable them in Settings > Between the News."
          : // A plugin call failed inside the native app — surface the real
            // error so it can be diagnosed from a tester's screenshot.
            `Notification error: ${lastPushError || "plugin unavailable"}`
      );
    }
    setTimeout(() => setPushNote(""), 6000);
  };

  return (
    <header className="header">
      <div className="header-top">
        <div className="logo">
          <div className="logo-icon">B</div>
          <div>
            <div className="logo-text">
              BETWEEN <span>THE NEWS</span>
            </div>
            <div className="header-subtitle">Media criticism &middot; we review the coverage</div>
          </div>
        </div>
        <div className="header-actions">
          <button
            className={`header-btn ${pushStatus === "granted" ? "header-btn-active" : ""}`}
            title={pushStatus === "granted" ? "Notifications on" : "Turn on notifications"}
            onClick={handleBell}
          >
            {"\u{1F514}"}
          </button>
        </div>
      </div>
      {pushNote && <div className="push-note">{pushNote}</div>}
    </header>
  );
}

// ===== POST CARD =====
function PostCard({ post, onOpen, featured }) {
  const { votes, handleVote } = useVote(post.id, post.upvotes, post.downvotes);
  const [copied, setCopied] = useState(false);
  const watchedOutlets = useWatchlist();
  const watching = watchedOutlets.includes(String(post.source || "").toLowerCase());

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = `${SITE_URL}${analysisPath(post)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.headline, url });
        return;
      } catch (err) {
        return; // user cancelled the share sheet
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // clipboard unavailable — nothing more we can do
    }
  };

  return (
    <div className={`post-card ${featured ? "featured" : ""}`} onClick={() => onOpen(post)}>
      <div className="post-card-source">
        <span className="critique-label">Critique of</span>
        <span className="critique-outlet">
          <span className="source-dot" style={{ background: post.sourceColor }} />
          {post.source}
        </span>
        <button
          className={`watch-btn ${watching ? "watching" : ""}`}
          title={watching ? `Stop watching ${post.source}` : `Watch ${post.source}`}
          onClick={(e) => {
            e.stopPropagation();
            toggleWatched(post.source);
          }}
        >
          {watching ? "\u{2713} Watching" : "+ Watch"}
        </button>
      </div>
      <div className="post-card-body">
        <Link
          to={analysisPath(post)}
          className="post-card-headline"
          style={{ display: "block", color: "inherit", textDecoration: "none" }}
          onClick={(e) => e.stopPropagation()}
        >
          {post.headline}
        </Link>
        <div className="post-card-preview">
          {(post.decoded || post.pullQuote || "").substring(0, 180)}...
        </div>
        <div className="post-card-tags">
          {(post.factTags || []).slice(0, 3).map((tag, i) => (
            <span className="fact-tag" key={i}>{tag}</span>
          ))}
        </div>
      </div>
      <div className="post-card-footer">
        <button
          className={`vote-btn up ${votes.voted === "up" ? "active" : ""}`}
          onClick={(e) => { e.stopPropagation(); handleVote("up"); }}
          title="This critique is fair"
        >
          Fair {votes.up}
        </button>
        <button
          className={`vote-btn down ${votes.voted === "down" ? "active" : ""}`}
          onClick={(e) => { e.stopPropagation(); handleVote("down"); }}
          title="This critique is too harsh"
        >
          Too harsh {votes.down}
        </button>
        <span className="comment-btn">
          {"\u{1F4AC}"} {post.comments || 0}
        </span>
        <button className="share-btn" onClick={handleShare} title="Share">
          {copied ? "✓" : "\u{1F517}"}
        </button>
      </div>
    </div>
  );
}

// ===== POST DETAIL =====
// Score every other analysis for topical overlap with this one (shared
// headline/fact-tag words, same outlet) so each article page links to a
// handful of genuinely related pieces with descriptive anchor text.
const RELATED_STOPWORDS = new Set([
  "the","a","an","and","or","but","of","in","on","to","for","its","it","is",
  "was","were","are","how","why","what","who","when","with","at","by","as",
  "from","this","that","these","after","before","over","under","not","no",
  "he","she","they","his","her","their","one","two","new","news","says",
  "said","about","into","out","all","you","your","our","between","owner",
]);

function relatedTokens(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 2 && !RELATED_STOPWORDS.has(w));
}

function relatedAnalyses(post, analyses, max = 6) {
  const base = new Set([
    ...relatedTokens(post.headline),
    ...(post.factTags || []).flatMap(relatedTokens),
  ]);
  return analyses
    .filter((a) => a.id !== post.id)
    .map((a) => {
      let score = 0;
      relatedTokens(a.headline).forEach((w) => { if (base.has(w)) score += 2; });
      (a.factTags || []).flatMap(relatedTokens).forEach((w) => { if (base.has(w)) score += 1; });
      if (canonOutletName(a.source) === canonOutletName(post.source)) score += 2;
      return { a, score };
    })
    .filter((x) => x.score >= 4)
    .sort((x, y) => y.score - x.score || y.a.id - x.a.id)
    .slice(0, max)
    .map((x) => x.a);
}

function PostDetail({ post, onBack, analyses = [] }) {
  const { votes, handleVote } = useVote(post.id, post.upvotes, post.downvotes);
  const decodedBody = useAnalysisBody(post);
  const { comments, addComment, reportComment, blockAuthor, unblockAllAuthors, blockedCount } = useComments(post.id);
  const { outlets } = useMediaGuide();
  const sourceOutlet = outlets.find((o) => sourceMatchesOutlet(post.source, o.name));
  // If this article IS an outlet deep-dive ("Is X biased?"), link back to that
  // outlet's Media Guide profile so the two pages reference each other.
  const deepDiveKey = Object.keys(OUTLET_DEEP_DIVES).find(
    (k) => OUTLET_DEEP_DIVES[k] === post.number
  );
  const deepDiveOutlet = deepDiveKey
    ? outlets.find((o) => sourceMatchesOutlet(o.name, deepDiveKey))
    : null;
  const [commentText, setCommentText] = useState("");
  const [copied, setCopied] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(hasAcceptedPostingTerms);
  const [commentError, setCommentError] = useState("");

  const handleAddComment = () => {
    if (!termsAccepted) {
      setCommentError("Please agree to the posting rules first.");
      return;
    }
    const verdict = checkText(commentText);
    if (!verdict.ok) {
      setCommentError(verdict.reason);
      return;
    }
    setCommentError("");
    addComment(commentText.trim(), "You");
    setCommentText("");
  };

  const isTracker = post.category === "Tracker";
  const sections = isTracker ? parseTrackerSections(decodedBody) : null;
  const plainBody = ((isTracker
    ? `${sections.tldr.join(" ")} ${sections.body}`.trim()
    : decodedBody) || "")
    .replace(/\[([^\]\n]+)\]\(\/[^\s)]+\)/g, "$1")
    .replace(/\n\s*\*\s*\n/g, " ")
    .replace(/^#{2,3}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1");

  const canonicalUrl = `${SITE_URL}${analysisPath(post)}`;
  const seo = seoFor(post);
  const metaDescription = seo.dek || firstSentences(plainBody, 155);
  const pageTitle = searchTitle(post);
  const noindex = isNoindex(post);
  const earlierFromUs = (seo.related_ids || [])
    .map((rid) => analyses.find((a) => Number(a.id) === Number(rid)))
    .filter(Boolean);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: post.headline, url: canonicalUrl });
        return;
      } catch (err) {
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(canonicalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // clipboard unavailable
    }
  };

  // FAQPage markup stopped producing rich results in May 2026, so trackers keep
  // their visible Q&A but we no longer emit the schema for it.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": post.category === "Guide" || isTracker ? "Article" : "NewsArticle",
    headline: post.headline,
    description: metaDescription,
    image: [`${SITE_URL}/og/${post.id}.png`],
    datePublished: post.created_at || post.date,
    dateModified: post.updated_at || post.created_at || post.date,
    author: { "@type": "Organization", name: "Between the News", url: `${SITE_URL}/about/` },
    publisher: {
      "@type": "Organization",
      name: "Between the News",
      url: `${SITE_URL}/`,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo512.png` },
    },
    mainEntityOfPage: canonicalUrl,
    isAccessibleForFree: true,
    inLanguage: "en",
  };
  if (seo.source_url) {
    // The criticized (or praised) article is the thing this piece is about.
    jsonLd.citation = [seo.source_url, seo.contrast_url].filter(Boolean);
  }
  if (post.source && post.source !== "Between the News") {
    jsonLd.about = { "@type": "NewsMediaOrganization", name: post.source };
  }

  return (
    <main className="post-detail">
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex,follow" />}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={post.headline} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={`${SITE_URL}/og/${post.id}.png`} />
      <meta property="article:published_time" content={post.created_at || ""} />
      <meta property="article:modified_time" content={post.updated_at || post.created_at || ""} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={post.headline} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={`${SITE_URL}/og/${post.id}.png`} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <button className="detail-back" onClick={onBack}>
        {"←"} Back
      </button>
      <article>

      <div className="detail-source-bar">
        <div>
          <div className="critique-label">
            {isTracker || post.category === "Guide" ? "Media guide" : "Our critique of"}
          </div>
          {sourceOutlet ? (
            <Link
              to={outletPath(sourceOutlet)}
              className="detail-source-name"
              style={{
                color: "inherit",
                display: "block",
                textDecoration: "underline",
                textDecorationThickness: 1,
                textUnderlineOffset: 3,
              }}
              title={`Who owns ${sourceOutlet.name}? See the ownership & bias profile`}
            >
              {post.source}
            </Link>
          ) : (
            <div className="detail-source-name">
              <span className="source-dot" style={{ background: post.sourceColor }} />
              {post.source}
            </div>
          )}
          <div className="detail-source-meta">
            {isTracker ? (
              <>
                Published {post.date}
                {formatDay(post.updated_at) && (
                  <> &middot; Updated {formatDay(post.updated_at)}</>
                )}{" "}
                &middot; {post.readTime}
              </>
            ) : post.category === "Guide" ? (
              <>
                Published {post.date} &middot; Last reviewed{" "}
                {formatDay(post.updated_at || post.created_at) || post.date} &middot;{" "}
                {post.readTime}
              </>
            ) : (
              <>
                <time dateTime={post.created_at || undefined}>{post.date}</time> &middot;{" "}
                {post.readTime}
              </>
            )}
          </div>
        </div>
        <span
          className={`category-badge ${post.category.toLowerCase()}`}
          style={{ marginLeft: "auto" }}
        >
          {post.category}
        </span>
      </div>

      <div style={{ padding: "20px 0 0" }}>
        <ReadingSize />
        <h1 className="detail-headline">{post.headline}</h1>
        <div className="byline">
          By the Between the News desk &middot;{" "}
          <Link to="/about/#how-we-work">researched with AI tools, checked and edited by a human editor</Link>
        </div>

        <div className="detail-tags">
          {(post.factTags || []).map((tag, i) => (
            <span className="detail-tag" key={i}>{tag}</span>
          ))}
        </div>

        {deepDiveOutlet && (
          <Link
            to={outletPath(deepDiveOutlet)}
            style={{
              display: "inline-block",
              margin: "10px 0 0",
              fontSize: 13,
              fontWeight: 600,
              color: "#E63946",
              textDecoration: "none",
            }}
          >
            {deepDiveOutlet.name} in the Media Guide: ownership, bias rating and every analysis {"→"}
          </Link>
        )}

        {isTracker && sections.tldr.length > 0 && (
          <div className="tldr-box">
            <div className="tldr-title">TL;DR</div>
            <ul className="tldr-list">
              {sections.tldr.map((t, i) => (
                <li key={i}>{renderRichText(t, `tldr${i}`)}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="decoded-section">
          <div className="decoded-header">
            <span className="decoded-icon">{isTracker ? "\u{1F4CC}" : "\u{1F441}"}</span>
            <span className="decoded-title">{isTracker ? "The Full Picture" : "Decoded"}</span>
          </div>
          <div className="decoded-text decoded-paragraphs">
            {bodyParagraphs(isTracker ? sections.body : decodedBody).map((p, i) => {
              const heading = p.match(/^#{2,3}\s+(.+)/);
              return (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <div className="para-sep" aria-hidden="true">
                      *
                    </div>
                  )}
                  {heading ? (
                    <h3 className="decoded-heading">{renderRichText(heading[1], `h${i}`)}</h3>
                  ) : (
                    <p>{renderRichText(p, `p${i}`)}</p>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {(seo.source_url || seo.contrast_url) && (
          <div className="source-links">
            <div className="source-links-title">The coverage we&apos;re talking about</div>
            <ul>
              {seo.source_url && (
                <li>
                  <a href={seo.source_url} target="_blank" rel="noopener noreferrer">
                    {seo.source_label || seo.source_url}
                  </a>
                </li>
              )}
              {seo.contrast_url && (
                <li>
                  <a href={seo.contrast_url} target="_blank" rel="noopener noreferrer">
                    {seo.contrast_label || seo.contrast_url}
                  </a>
                </li>
              )}
            </ul>
            <div className="source-links-note">
              We quote outlets briefly and link to the original so you can read the full article and judge for yourself.
            </div>
          </div>
        )}

        {earlierFromUs.length > 0 && (
          <div className="source-links">
            <div className="source-links-title">Earlier from us on this story</div>
            <ul>
              {earlierFromUs.map((r) => (
                <li key={r.id}>
                  <Link to={analysisPath(r)}>{r.headline}</Link>
                  <span className="related-meta"> &middot; {r.source} &middot; {r.date}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isTracker && sections.faq.length > 0 && (
          <div className="faq-section">
            <div className="faq-header">{"❓"} Frequently Asked Questions</div>
            {sections.faq.map((f, i) => (
              <div className="faq-item" key={i}>
                <div className="faq-q">{renderRichText(f.q, `faqq${i}`)}</div>
                <div className="faq-a">{renderRichText(f.a, `faqa${i}`)}</div>
              </div>
            ))}
          </div>
        )}

        {isTracker && (
          <div className="hub-backlink">
            <a href="/trackers/">More live trackers &amp; tools {"→"}</a>
          </div>
        )}

        {post.pullQuote && (
          <div className="pull-quote">&ldquo;{post.pullQuote}&rdquo;</div>
        )}

        {post.apComparison && (
          <div className="ap-comparison">
            <div className="ap-comparison-header">
              {"\u{1F4CA}"} AP / Reuters Comparison
            </div>
            <div className="ap-comparison-text">{post.apComparison}</div>
          </div>
        )}

        <div className="detail-voting">
          <button
            className={`detail-vote-btn up ${votes.voted === "up" ? "active" : ""}`}
            onClick={() => handleVote("up")}
          >
            {"\u{1F44D}"} {votes.up}
          </button>
          <button
            className={`detail-vote-btn down ${votes.voted === "down" ? "active" : ""}`}
            onClick={() => handleVote("down")}
          >
            {"\u{1F44E}"} {votes.down}
          </button>
          <button className="detail-vote-btn" onClick={handleShare}>
            {copied ? "✓ Copied" : `${"\u{1F517}"} Share`}
          </button>
        </div>

        {(() => {
          const related = relatedAnalyses(post, analyses);
          return (
            <div className="related-section">
              {related.length > 0 && (
                <>
                  <div className="comments-header">Related on Between the News</div>
                  <ul className="related-list">
                    {related.map((r) => (
                      <li key={r.id} className="related-item">
                        <Link to={analysisPath(r)} className="related-link">
                          {r.headline}
                        </Link>
                        <span className="related-meta">
                          {" "}&middot; {r.source} &middot; {r.date}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div className="related-hubs">
                {sourceOutlet && (
                  <Link to={outletPath(sourceOutlet)}>Who owns {sourceOutlet.name}? Bias &amp; ownership profile</Link>
                )}
                <Link to="/guide/">All 20 outlets in the Media Guide</Link>
                <Link to="/trackers/">Live trackers &amp; tools</Link>
                <Link to="/explore/">Search the archive</Link>
              </div>
            </div>
          );
        })()}

        {!IS_PRERENDER && (
        <div className="comments-section">
          <div className="comments-header">
            Comments <span>({comments.length})</span>
          </div>
          {blockedCount > 0 && (
            <div className="comment-moderation-note">
              You&apos;ve blocked {blockedCount} author{blockedCount > 1 ? "s" : ""} on this device.{" "}
              <button className="comment-action" onClick={unblockAllAuthors}>
                Unblock all
              </button>
            </div>
          )}
          <div className="comment-input-row">
            <input
              className="comment-input"
              placeholder="Add a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
            />
            <button
              className="comment-submit"
              onClick={handleAddComment}
              disabled={!termsAccepted}
            >
              Post
            </button>
          </div>
          {!termsAccepted && (
            <label className="comment-terms-consent">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => {
                  if (e.target.checked) {
                    acceptPostingTerms();
                    setTermsAccepted(true);
                    setCommentError("");
                  }
                }}
              />
              <span>
                I agree to the <Link to="/terms">Terms of Use</Link>. There is no tolerance
                for objectionable content or abusive users: hate speech, harassment,
                threats, sexual content and spam are removed and posters are barred.
              </span>
            </label>
          )}
          {commentError && <div className="comment-error">{commentError}</div>}
          <div className="comment-moderation-note">
            Comments are screened before posting. Use <strong>Report</strong> to flag a
            comment — reports are reviewed by our editorial team within 24 hours — or{" "}
            <strong>Block</strong> to hide an author.
          </div>
          {comments.map((c, i) => (
            <div className="comment-item" key={c.id || i}>
              <div className="comment-author">{c.author}</div>
              <div className="comment-text">{c.text}</div>
              <div className="comment-meta-row">
                <div className="comment-time">{timeAgo(c.created_at)}</div>
                <div className="comment-actions">
                  <button
                    className="comment-action"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Report this comment as abusive or objectionable? Our editorial team reviews every report within 24 hours and removes content that breaks the Terms of Use."
                        )
                      ) {
                        reportComment(c);
                        window.alert(
                          "Thanks — this comment has been reported. We'll review it within 24 hours."
                        );
                      }
                    }}
                  >
                    Report
                  </button>
                  <button
                    className="comment-action"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Block ${c.author}? Their comments will no longer be shown on this device.`
                        )
                      ) {
                        blockAuthor(c.author);
                      }
                    }}
                  >
                    Block
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
      </article>
    </main>
  );
}

// ===== EXPLORE PAGE =====
function getTrendingPosts(analyses) {
  return [...analyses]
    .filter(inFeed)
    .sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0))
    .slice(0, 5);
}

function TrendingWidget({ analyses, onOpen }) {
  const posts = getTrendingPosts(analyses || []);
  return (
    <div className="trending-section">
      <div className="section-title">{"\u{1F525}"} Trending Topics</div>
      <div className="trending-list">
        {posts.map((post, i) => (
          <div
            className="trending-item"
            key={post.id}
            onClick={() => onOpen && onOpen(post)}
          >
            <span className="trending-rank">{i + 1}</span>
            <div className="trending-info">
              <h4>
                <Link
                  to={analysisPath(post)}
                  style={{ color: "inherit", textDecoration: "none" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {post.headline.length > 70
                    ? `${post.headline.slice(0, 70)}...`
                    : post.headline}
                </Link>
              </h4>
              <p>
                {post.upvotes || 0} upvotes &middot; {post.comments || 0} comments
              </p>
            </div>
          </div>
        ))}
        {posts.length === 0 && (
          <div className="trending-item">
            <div className="trending-info">
              <p>No trending topics yet</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== HOME SIDEBAR (desktop only) =====
function HomeSidebar({ analyses, outlets, onSeeGuide, onOpenPost }) {
  const topOutlets = outlets.filter((o) => o.tier === 1).slice(0, 4);
  return (
    <aside className="home-sidebar">
      <TrendingWidget analyses={analyses} onOpen={onOpenPost} />
      {topOutlets.length > 0 && (
        <div className="sidebar-guide">
          <div className="section-title">{"\u{1F4DA}"} Media Guide</div>
          <div className="sidebar-outlet-list">
            {topOutlets.map((o, i) => (
              <Link
                className="sidebar-outlet-item"
                key={i}
                to={outletPath(o)}
                style={{ color: "inherit", textDecoration: "none" }}
                title={`Who owns ${o.name}? Bias & ownership profile`}
              >
                <span className="outlet-color-bar" style={{ backgroundColor: o.color }} />
                <span className="sidebar-outlet-name">{o.name}</span>
                <span className="outlet-bias">{o.bias}</span>
              </Link>
            ))}
          </div>
          <Link className="sidebar-guide-link" to="/guide" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>
            See full guide {"→"}
          </Link>
        </div>
      )}
    </aside>
  );
}

function ExplorePage({ analyses, onOpenPost }) {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeOutlet, setActiveOutlet] = useState(null);

  const visibleAnalyses = analyses.filter(inFeed);

  // Outlets that actually have analyses, with counts, most-covered first
  const outletChips = Object.values(
    visibleAnalyses.reduce((acc, a) => {
      const key = canonOutletName(a.source);
      if (!acc[key]) acc[key] = { name: a.source, color: a.sourceColor, count: 0 };
      acc[key].count += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.count - a.count);

  const q = search.trim().toLowerCase();
  const filtered = visibleAnalyses.filter((a) => {
    if (q) {
      return (
        a.headline.toLowerCase().includes(q) ||
        a.source.toLowerCase().includes(q) ||
        (a.decoded || a.pullQuote || "").toLowerCase().includes(q) ||
        (a.factTags || []).some((t) => String(t).toLowerCase().includes(q))
      );
    }
    if (activeOutlet && !sourceMatchesOutlet(a.source, activeOutlet)) return false;
    if (activeCategory !== "All" && a.category !== activeCategory) return false;
    return true;
  });

  const isFiltering = q || activeOutlet || activeCategory !== "All";
  const resultsTitle = q
    ? `Results (${filtered.length})`
    : activeOutlet
    ? `${activeOutlet} (${filtered.length})`
    : activeCategory !== "All"
    ? `${activeCategory} (${filtered.length})`
    : "\u{1F4F0} Latest Analyses";

  return (
    <div className="explore-page">
      <title>Explore Media Analyses — Between the News</title>
      <meta name="description" content="Search Between the News analyses by headline, outlet, or topic — and browse coverage critiques outlet by outlet." />
      <link rel="canonical" href={`${SITE_URL}/explore/`} />
      <div className="explore-header">Explore</div>
      <div className="explore-subtitle">Search analyses, browse by outlet or topic</div>

      <div className="search-wrapper">
        <span className="search-icon">{"\u{1F50E}"}</span>
        <input
          className="search-bar"
          placeholder="Search headlines, sources..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="explore-cats">
        {["All", "Politics", "Weird"].map((cat) => (
          <button
            key={cat}
            className={`category-pill ${activeCategory === cat ? "active" : ""}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {!isFiltering && <TrendingWidget analyses={analyses} onOpen={onOpenPost} />}

      <div className="section-title">{"\u{1F4FA}"} Browse by outlet</div>
      <div className="outlet-chip-grid">
        {outletChips.map((o) => {
          const active = activeOutlet && sourceMatchesOutlet(o.name, activeOutlet);
          return (
            <button
              key={o.name}
              className={`outlet-chip ${active ? "active" : ""}`}
              onClick={() => setActiveOutlet(active ? null : o.name)}
            >
              <span className="chip-dot" style={{ backgroundColor: o.color || "#e63946" }} />
              {o.name}
              <span className="chip-count">{o.count}</span>
            </button>
          );
        })}
      </div>

      <div>
        <div className="section-title">{resultsTitle}</div>
        <div className="feed">
          {filtered.map((post) => (
            <PostCard key={post.id} post={post} onOpen={onOpenPost} />
          ))}
          {filtered.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">{"\u{1F50D}"}</div>
              <h3>No results</h3>
              <p>Try a different search term or filter</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== TRACKERS & TOOLS HUB =====
// Not linked from the nav (readers browse the editorial feed) — exists as a
// crawlable internal-linking hub so search engines find every tracker and tool.
const TOOLS_LIST = [
  {
    name: "Tariff Cost Calculator",
    path: "/tools/tariff-calculator/",
    blurb: "What the 2026 tariffs add to a purchase — today vs. after the July 24 cliff.",
  },
];

function TrackersHub({ analyses }) {
  const trackers = analyses.filter((a) => a.category === "Tracker");
  return (
    <div className="hub-page">
      <title>Live News Trackers & Tools — Between the News</title>
      <meta
        name="description"
        content="Continuously updated news trackers and free interactive tools from Between the News: tariffs, the US–Iran conflict, the World Cup and more."
      />
      <link rel="canonical" href={`${SITE_URL}/trackers/`} />
      <div className="explore-header">Trackers &amp; Tools</div>
      <div className="explore-subtitle">Living pages we keep updated as the story moves</div>

      <div className="section-title">{"\u{1F4CC}"} Trackers</div>
      {trackers.map((t) => (
        <Link className="hub-item" key={t.id} to={analysisPath(t)}>
          <div className="hub-item-title">{t.headline}</div>
          <div className="hub-item-meta">Updated {formatDay(t.updated_at) || t.date}</div>
        </Link>
      ))}
      {trackers.length === 0 && (
        <div className="hub-item-meta" style={{ padding: "0 16px" }}>New trackers coming soon.</div>
      )}

      <div className="section-title">{"\u{1F9EE}"} Tools</div>
      {TOOLS_LIST.map((tool) => (
        <Link className="hub-item" key={tool.path} to={tool.path}>
          <div className="hub-item-title">{tool.name}</div>
          <div className="hub-item-meta">{tool.blurb}</div>
        </Link>
      ))}
    </div>
  );
}

// ===== COMMUNITY PAGE =====
function formatCount(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}

function CommunityPage({ analyses, onOpenPost }) {
  const { recentComments, commentCount, addSuggestion } = useCommunityData();
  const [suggestion, setSuggestion] = useState("");
  const [suggestStatus, setSuggestStatus] = useState("idle"); // idle | sending | done | error
  const [suggestError, setSuggestError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(hasAcceptedPostingTerms);

  const feedAnalyses = analyses.filter(inFeed);
  const totalVotes = analyses.reduce(
    (sum, a) => sum + (a.upvotes || 0) + (a.downvotes || 0),
    0
  );
  const mostDiscussed = getTrendingPosts(analyses);
  const findAnalysis = (id) => analyses.find((a) => String(a.id) === String(id));

  const submitSuggestion = async () => {
    if (suggestStatus === "sending") return;
    if (!termsAccepted) {
      setSuggestError("Please agree to the posting rules first.");
      return;
    }
    const verdict = checkText(suggestion);
    if (!verdict.ok) {
      setSuggestError(verdict.reason);
      return;
    }
    setSuggestError("");
    setSuggestStatus("sending");
    const ok = await addSuggestion(suggestion);
    if (ok) {
      setSuggestion("");
      setSuggestStatus("done");
    } else {
      setSuggestStatus("error");
    }
  };

  return (
    <div className="community-page">
      <title>Community — Between the News</title>
      <meta name="description" content="Discuss media bias and editorial framing with the Between the News community." />
      <link rel="canonical" href={`${SITE_URL}/community/`} />
      <div className="community-header">Community</div>
      <div className="community-subtitle">Discuss analyses and media literacy</div>

      <div className="community-stats">
        <div className="stat-card">
          <div className="stat-number">{formatCount(feedAnalyses.length)}</div>
          <div className="stat-label">Analyses</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{formatCount(commentCount)}</div>
          <div className="stat-label">Comments</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{formatCount(totalVotes)}</div>
          <div className="stat-label">Votes</div>
        </div>
      </div>

      <div className="suggest-box">
        <div className="suggest-title">{"\u{1F4E3}"} Suggest a headline to decode</div>
        <div className="suggest-sub">
          Seen a headline that deserves a closer look? Paste it (or a link) below —
          the best suggestions become analyses.
        </div>
        {suggestStatus === "done" ? (
          <div className="suggest-done">✓ Got it — thanks! We read every suggestion.</div>
        ) : (
          <>
            <div className="comment-input-row">
              <input
                className="comment-input"
                placeholder="Paste a headline or link..."
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitSuggestion()}
              />
              <button
                className="comment-submit"
                onClick={submitSuggestion}
                disabled={suggestStatus === "sending" || !termsAccepted}
              >
                {suggestStatus === "sending" ? "..." : "Send"}
              </button>
            </div>
            {!termsAccepted && (
              <label className="comment-terms-consent">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => {
                    if (e.target.checked) {
                      acceptPostingTerms();
                      setTermsAccepted(true);
                      setSuggestError("");
                    }
                  }}
                />
                <span>
                  I agree to the <Link to="/terms">Terms of Use</Link>. There is no tolerance
                  for objectionable content or abusive users.
                </span>
              </label>
            )}
            {suggestError && <div className="comment-error">{suggestError}</div>}
            {suggestStatus === "error" && (
              <div className="newsletter-error">Something went wrong — try again.</div>
            )}
          </>
        )}
      </div>

      <div className="section-title">{"\u{1F525}"} Most discussed</div>
      {mostDiscussed.map((post) => (
        <div
          className="discussion-card"
          key={post.id}
          onClick={() => onOpenPost && onOpenPost(post)}
          style={{ cursor: "pointer" }}
        >
          <h4>{post.headline}</h4>
          <div className="discussion-meta">
            <span>{post.source}</span>
            <span>{"\u{1F44D}"} {post.upvotes || 0}</span>
            <span>{"\u{1F4AC}"} {post.comments || 0}</span>
          </div>
        </div>
      ))}

      <div className="section-title">{"\u{1F4AC}"} Latest comments</div>
      {recentComments.map((c) => {
        const parent = c.analysis_id ? findAnalysis(c.analysis_id) : null;
        return (
          <div className="recent-comment" key={c.id}>
            <div className="recent-comment-author">
              {c.author}
              {!c.analysis_id && <span className="suggestion-tag">filed on the complaint line</span>}
              <span className="recent-comment-time">{timeAgo(c.created_at)}</span>
            </div>
            <div className="recent-comment-text">{displayCommentText(c.text)}</div>
            {!c.analysis_id && (
              <Link className="recent-comment-link" to="/complaint-line">
                see the complaint line {"→"}
              </Link>
            )}
            {parent && (
              <Link className="recent-comment-link" to={analysisPath(parent)}>
                on: {parent.headline.length > 80 ? `${parent.headline.slice(0, 80)}...` : parent.headline} {"→"}
              </Link>
            )}
          </div>
        );
      })}
      {recentComments.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">{"\u{1F4AC}"}</div>
          <h3>No comments yet</h3>
          <p>Open any analysis and be the first to comment.</p>
        </div>
      )}
    </div>
  );
}

// ===== ABOUT PAGE (E-E-A-T: who we are, method, corrections) =====
function AboutPage() {
  return (
    <div className="explore-page">
      <title>About Us — Between the News</title>
      <meta
        name="description"
        content="Who runs Between the News, how our media-criticism analyses are researched and verified, and how to reach us or request a correction."
      />
      <link rel="canonical" href={`${SITE_URL}/about/`} />
      <div className="explore-header">About Between the News</div>
      <div className="explore-subtitle">Catch the intent between the news — who we are and how we work</div>

      <div className="outlet-detail-section">
        <h3>What this site is</h3>
        <p>
          Between the News is an independent media-criticism publication based in Dublin, Ireland.
          We don't report the news — we examine how it gets reported. Every analysis looks at the
          choices behind the coverage: the words a headline used, who was made the subject of the
          sentence, what got buried in paragraph nine, and how differently two outlets told you the
          exact same story.
        </p>
      </div>

      <div className="outlet-detail-section">
        <h3>Who we are</h3>
        <p>
          Between the News was founded and is edited by a professional journalist with newsroom
          experience, based in Dublin. The site is fully independent: no outlet, party, or
          corporate owner is behind it, and we run no advertising.
        </p>
      </div>

      <div className="outlet-detail-section" id="how-we-work">
        <h3>How we work, and where AI fits in</h3>
        <p>
          We use AI tools to help research and draft our analyses: scanning the day's coverage
          across twenty outlets, pulling the exact headlines and quotes, and producing a first
          draft. A human editor decides which stories are worth a piece, sets the angle, checks
          every quote against the original article, and approves each piece before it is
          published. Nothing goes live unread.
        </p>
        <p>
          We link to the article we are criticising or praising whenever we can, so you can read
          it in full and judge for yourself. Vote counts and comments on this site are real: we
          do not seed, buy or invent engagement. If you ever spot a number that looks off, tell us.
        </p>
      </div>

      <div className="outlet-detail-section">
        <h3>How analyses are made</h3>
        <p>
          Every quoted headline or phrase is checked against the outlet's actual published wording
          before we name names — we never invent or paraphrase a quote and present it as real.
          Each piece cross-references at least two outlets' coverage of the same story. We cover a
          fixed set of twenty major outlets across the US, UK and the rest of the world, and we
          grade the coverage, not the politics of the people in it — praise and criticism land on
          the same outlet in the same week when the coverage earns both.
        </p>
        <p>
          Three checks run before anything is published. First, the primary source: we open the
          outlet's own article — not a search snippet, not someone's screenshot — and read past
          paragraph nine, because that's usually where the headline's counter-evidence lives.
          Second, the outlet's own history: before calling a framing choice a habit, we look at
          how the same newsroom handled the same kind of story before, including in our own
          archive. Third, the cross-check: if every outlet framed a story the same way, that's an
          industry-wide pattern and we say so — we don't pin it on one newsroom.
        </p>
        <p>
          When we get something wrong anyway, the fix is made in the piece itself, visibly — not
          quietly deleted.
        </p>
      </div>

      <div className="outlet-detail-section">
        <h3>Where to start</h3>
        <p>
          The <Link to="/">home feed</Link> carries the latest analyses. The{" "}
          <Link to="/guide">Media Guide</Link> profiles all twenty outlets we cover — who owns
          each one, how it leans, and every analysis we've written about it.{" "}
          <Link to="/trackers">Trackers &amp; tools</Link> collects our running reference pages,
          and <Link to="/explore">Explore</Link> lets you search the full archive by outlet,
          topic, or phrase.
        </p>
      </div>

      <div className="outlet-detail-section">
        <h3>Corrections</h3>
        <p>
          If we got a quote, attribution, or fact wrong, we want to know. Email{" "}
          <a href="mailto:corrections@betweenthenews.com">corrections@betweenthenews.com</a> and
          we'll review it against the source material and fix errors visibly.
        </p>
      </div>

      <div className="outlet-detail-section">
        <h3>Contact</h3>
        <p>
          General: <a href="mailto:hello@betweenthenews.com">hello@betweenthenews.com</a>
          <br />
          Privacy: <a href="mailto:privacy@betweenthenews.com">privacy@betweenthenews.com</a>
        </p>
      </div>
    </div>
  );
}

// ===== MEDIA GUIDE PAGE =====
function OutletDetailPage({ analyses, outlets, onBack }) {
  const { slug } = useParams();
  const outlet = outlets.find((o) => slugify(o.name) === slug);

  if (!outlet) {
    return (
      <div className="outlet-detail">
        <button className="detail-back" onClick={onBack}>{"←"} Back to Guide</button>
        <div className="empty-state">
          <div className="empty-state-icon">{"\u{1F50D}"}</div>
          <h3>Outlet not found</h3>
        </div>
      </div>
    );
  }

  const aboutText = (outlet.description || "").split("###LANDMARK###")[0].trim();
  const hasLandmark = (outlet.description || "").includes("###LANDMARK###");
  const landmarkText = hasLandmark ? (outlet.description || "").split("###LANDMARK###")[1].trim() : "";
  const pageTitle = `Is ${outlet.name} biased? Who owns it`;
  const pageDescription = firstSentences(
    `${outlet.name}: owned by ${outlet.owner}. Our bias reading: ${outlet.bias}. ${aboutText}`,
    155
  );
  const deepDive = deepDiveFor(analyses, outlet.name);
  const outletPieces = analyses
    .filter((a) => sourceMatchesOutlet(a.source, outlet.name) && inFeed(a))
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const recentPieces = outletPieces.slice(0, 15);
  const lastPiece = outletPieces[0];

  const outletJsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    name: pageTitle,
    description: pageDescription,
    url: `${SITE_URL}${outletPath(outlet)}`,
    dateModified: (lastPiece && lastPiece.created_at) || outlet.created_at,
    publisher: { "@type": "Organization", name: "Between the News", url: `${SITE_URL}/` },
    mainEntity: {
      "@type": "NewsMediaOrganization",
      name: outlet.name,
      description: aboutText,
    },
  };

  return (
    <div className="outlet-detail">
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={`${SITE_URL}${outletPath(outlet)}`} />
      <script type="application/ld+json">{JSON.stringify(outletJsonLd)}</script>
      <button className="detail-back" onClick={onBack}>
        {"←"} Back to Guide
      </button>
      <div className="outlet-detail-header">
        <div className="outlet-detail-color" style={{ backgroundColor: outlet.color }}>
          {outlet.name[0]}
        </div>
        <div className="outlet-detail-info">
          <h1>{outlet.name}</h1>
          <p>{outlet.country} &middot; Bias: {outlet.bias}</p>
        </div>
      </div>
      <div className="outlet-detail-section">
        <h3>Owner</h3>
        <p>{outlet.owner}</p>
      </div>
      <div className="outlet-detail-section">
        <h3>About</h3>
        <p>{aboutText}</p>
      </div>
      {hasLandmark && (
        <div className="outlet-detail-section">
          <h3>Landmark Story</h3>
          <p>{landmarkText}</p>
        </div>
      )}
      {deepDive && (
        <div className="outlet-detail-section">
          <h3>Deep Dive</h3>
          <Link className="outlet-analysis-link" to={analysisPath(deepDive)}>
            <span className="outlet-analysis-headline" style={{ fontSize: 13, fontWeight: 600 }}>
              {deepDive.headline} {"→"}
            </span>
          </Link>
        </div>
      )}
      <div className="outlet-detail-section">
        <h3>Latest on {outlet.name}</h3>
        {recentPieces.map((a) => (
          <Link className="outlet-analysis-link" key={a.id} to={analysisPath(a)}>
            <span className="analysis-number" style={{ marginRight: 8 }}>{a.date}</span>
            <span className="outlet-analysis-headline" style={{ fontSize: 13 }}>{a.headline}</span>
          </Link>
        ))}
        {outletPieces.length > recentPieces.length && (
          <p style={{ fontSize: 13 }}>
            <Link to="/explore/">
              {outletPieces.length - recentPieces.length} more {outlet.name} analyses in the archive {"→"}
            </Link>
          </p>
        )}
        {outletPieces.length === 0 && (
          <p style={{ color: "#6b6b80", fontSize: 13 }}>No analyses yet for this outlet.</p>
        )}
      </div>
      <div className="outlet-detail-section">
        <p style={{ fontSize: 13 }}>
          <Link to="/guide/">All outlets in the Media Guide</Link> &middot;{" "}
          <Link to="/trackers/">Live trackers &amp; tools</Link>
        </p>
      </div>
    </div>
  );
}

function MediaGuidePage({ analyses, onOpenOutlet }) {
  const { outlets } = useMediaGuide();

  const countFor = (outlet) =>
    (analyses || []).filter((a) => sourceMatchesOutlet(a.source, outlet.name) && inFeed(a)).length;

  const tiers = [
    { label: "Tier 1 — Daily Analysis", tier: 1 },
    { label: "Tier 2 — 3-4 Times/Week", tier: 2 },
    { label: "Tier 3 — Weekly", tier: 3 },
  ];

  return (
    <div className="media-guide-page">
      <title>Media Guide: who owns the news, and how it leans</title>
      <meta name="description" content="Who owns Fox News, the BBC, CNN, the Daily Mail and 20 other outlets, how each one leans, and every analysis we've written about them." />
      <link rel="canonical" href={`${SITE_URL}/guide/`} />
      <h1 className="media-guide-header">Media Guide</h1>
      <div className="media-guide-subtitle">Know who owns the news you read</div>
      {tiers.map((t) => {
        const tierOutlets = outlets.filter((o) => o.tier === t.tier);
        if (tierOutlets.length === 0) return null;
        return (
          <div className="tier-section" key={t.tier}>
            <div className="tier-label">{t.label}</div>
            {tierOutlets.map((outlet, i) => (
              <Link
                className="outlet-card"
                key={i}
                to={outletPath(outlet)}
                onClick={(e) => {
                  e.preventDefault();
                  onOpenOutlet(outlet);
                }}
              >
                <div className="outlet-card-header">
                  <div className="outlet-color-bar" style={{ backgroundColor: outlet.color }} />
                  <div>
                    <div className="outlet-name">{outlet.name}</div>
                    <div className="outlet-country">{outlet.country}</div>
                    <div className="outlet-owner">{"\u{1F511}"} {outlet.owner}</div>
                  </div>
                  <div className="outlet-card-right">
                    <span className="outlet-bias">{outlet.bias}</span>
                    {countFor(outlet) > 0 && (
                      <span className="outlet-count">
                        {countFor(outlet)} {countFor(outlet) === 1 ? "analysis" : "analyses"}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ===== ANALYSIS ROUTE (per-article page, gives every analysis its own URL) =====
function AnalysisRoute({ analyses, loading, onBack }) {
  const { id } = useParams();
  const post = analyses.find((a) => String(a.id) === String(id));

  // In-app <Link> navigation (related links, card headlines) keeps the old
  // scroll position by default — snap to the top whenever the article changes.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="empty-state" style={{ minHeight: "100vh" }}>
        <div className="empty-state-icon">{"\u{1F4F0}"}</div>
        <h3>Loading...</h3>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="empty-state" style={{ minHeight: "100vh" }}>
        <title>Analysis not found — Between the News</title>
        <meta name="robots" content="noindex" />
        <div className="empty-state-icon">{"\u{1F50D}"}</div>
        <h3>Analysis not found</h3>
        <button className="detail-back" onClick={onBack}>{"←"} Back home</button>
      </div>
    );
  }

  return <PostDetail post={post} onBack={onBack} analyses={analyses} />;
}

// ===== HOME FEED =====
function HomeFeed({ analyses, onOpenPost, onSeeGuide }) {
  const [activeCategory, setActiveCategory] = useState("All");
  const watchedOutlets = useWatchlist();
  // "Watching" only earns a place in the filter row once the reader has
  // actually picked someone — an empty filter that returns nothing is worse
  // than no filter at all.
  const categories = watchedOutlets.length
    ? ["All", "Watching", "Politics", "Weird"]
    : ["All", "Politics", "Weird"];
  const filteredAnalyses =
    activeCategory === "All"
      ? analyses.filter(inFeed)
      : activeCategory === "Watching"
      ? analyses.filter(
          (a) => inFeed(a) && watchedOutlets.includes(String(a.source || "").toLowerCase())
        )
      : analyses.filter((a) => a.category === activeCategory);
  const { outlets } = useMediaGuide();

  return (
    <>
      <title>Between the News: media criticism in plain English</title>
      <meta
        name="description"
        content="We don't cover the news, we cover the coverage: how Fox, the BBC, CNN, the Daily Mail and 16 other outlets word, frame and bury the same story."
      />
      <meta name="keywords" content="news analysis, media bias, media criticism, media literacy, news decode" />
      <link rel="canonical" href={`${SITE_URL}/`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Between the News — Catch the Intent Between the News" />
      <meta property="og:description" content="Independent media criticism and analysis." />
      <meta property="og:url" content={`${SITE_URL}/`} />
      <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "NewsMediaOrganization",
              "@id": `${SITE_URL}/#organization`,
              name: "Between the News",
              url: `${SITE_URL}/`,
              logo: `${SITE_URL}/logo512.png`,
              description: "Independent media criticism — how major news outlets frame, word and bury the stories they cover.",
            },
            {
              "@type": "WebSite",
              "@id": `${SITE_URL}/#website`,
              name: "Between the News",
              url: `${SITE_URL}/`,
              publisher: { "@id": `${SITE_URL}/#organization` },
            },
          ],
        })}
      </script>
      <Header />
      {/* The masthead exists to remove an ambiguity that cost us an App Review
          rejection: nothing below is a news story. Every card is our own
          critique of somebody else's coverage, and the reader can commission
          the next one. Don't quietly drop this for being "wordy". */}
      <div className="masthead">
        <div className="masthead-line">
          <strong>This is not a news feed.</strong> We don&apos;t carry anyone&apos;s
          reporting. Every item below is our own critique of how an outlet covered a
          story — the words its headline chose, what it led with and what it buried.
        </div>
        <Link className="masthead-cta" to="/complaint-line">
          {"\u{1F4E3}"} Seen coverage that deserves scrutiny? File it with us &rarr;
        </Link>
      </div>
      <div className="categories">
        {categories.map((cat) => (
          <button
            key={cat}
            className={`category-pill ${activeCategory === cat ? "active" : ""}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>
      <div className="home-layout">
        <div className="home-main">
          <div className="feed" style={{ marginTop: 8 }}>
            {filteredAnalyses.map((post, i) => (
              <PostCard key={post.id} post={post} onOpen={onOpenPost} featured={i === 0} />
            ))}
            {filteredAnalyses.length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">{"\u{1F4F0}"}</div>
                <h3>No analyses yet</h3>
                <p>New analyses in this category coming soon.</p>
              </div>
            )}
          </div>
        </div>
        <HomeSidebar analyses={analyses} outlets={outlets} onSeeGuide={onSeeGuide} onOpenPost={onOpenPost} />
      </div>
    </>
  );
}

// ===== MAIN APP =====
function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const { analyses, loading, offline, refetch } = useAnalyses();
  const { outlets } = useMediaGuide();

  const handleOpenPost = (post) => {
    navigate(analysisPath(post));
    window.scrollTo(0, 0);
  };

  const handleOpenOutlet = (outlet) => {
    navigate(outletPath(outlet));
    window.scrollTo(0, 0);
  };

  const handleBackToGuide = () => {
    navigate("/guide");
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    navigate("/");
    window.scrollTo(0, 0);
  };

  const handleNavigate = (id) => {
    navigate(id === "home" ? "/" : `/${id}`);
    window.scrollTo(0, 0);
  };

  const activeTab = location.pathname === "/" ? "home" : location.pathname.split("/")[1];

  if (loading && location.pathname !== "/privacy") {
    return (
      <div className="app">
        <div className="empty-state" style={{ minHeight: "100vh" }}>
          <div className="empty-state-icon">{"\u{1F4F0}"}</div>
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {offline && (
        <div className="offline-banner" onClick={refetch}>
          You&apos;re offline &mdash; showing saved articles. Tap to retry.
        </div>
      )}
      <Routes>
        <Route
          path="/"
          element={
            <HomeFeed
              analyses={analyses}
              onOpenPost={handleOpenPost}
              onSeeGuide={() => navigate("/guide")}
            />
          }
        />
        <Route path="/explore" element={<ExplorePage analyses={analyses} onOpenPost={handleOpenPost} />} />
        <Route path="/community" element={<CommunityPage analyses={analyses} onOpenPost={handleOpenPost} />} />
        <Route path="/complaint-line" element={<ComplaintLine />} />
        <Route path="/guide" element={<MediaGuidePage analyses={analyses} onOpenOutlet={handleOpenOutlet} />} />
        <Route
          path="/guide/:slug"
          element={<OutletDetailPage analyses={analyses} outlets={outlets} onBack={handleBackToGuide} />}
        />
        <Route
          path="/analysis/:id/*"
          element={<AnalysisRoute analyses={analyses} loading={loading} onBack={handleBack} />}
        />
        <Route path="/tools/tariff-calculator" element={<TariffCalculator />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/trackers" element={<TrackersHub analyses={analyses} />} />
        <Route path="/privacy" element={<PrivacyPolicy onBack={handleBack} />} />
        <Route path="/terms" element={<TermsOfUse onBack={handleBack} />} />
        <Route path="/admin" element={<AdminPanel onBack={() => { navigate("/"); refetch(); }} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {location.pathname !== "/admin" && location.pathname !== "/privacy" && (
        <>
          <Footer onPrivacyClick={() => navigate("/privacy")} />
          <BottomNav active={activeTab} onNavigate={handleNavigate} />
          {!IS_PRERENDER && <CookieConsent />}
        </>
      )}
    </div>
  );
}

export default App;
